import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import { 
  Calendar, Plus, Edit2, Trash2, X, Save, Users, 
  Clock, CheckCircle, AlertCircle, ChevronDown 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
const API = API_BASE;

export default function AcademyCohortManager() {
  const [cohorts, setCohorts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCohort, setEditingCohort] = useState(null);
  const [filterCourse, setFilterCourse] = useState('all');

  const [formData, setFormData] = useState({
    course_id: '',
    course_title: '',
    start_date: '',
    end_date: '',
    enrollment_deadline: '',
    max_seats: 30,
    notes: ''
  });

  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    fetchCohorts();
    fetchCourses();
  }, [showArchived]);

  const fetchCohorts = async () => {
    try {
      const response = await axios.get(`${API}/api/ica/cohorts?active_only=${!showArchived}`);
      setCohorts(response.data.cohorts || []);
    } catch (error) {
      console.error('Failed to fetch cohorts:', error);
      toast.error('Failed to load cohorts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/api/ica/courses-list`);
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const handleAddCohort = async (e) => {
    e.preventDefault();
    
    if (!formData.course_id || !formData.start_date) {
      toast.error('Please select a course and start date');
      return;
    }

    try {
      const selectedCourse = courses.find(c => c.course_id === formData.course_id);
      const payload = {
        ...formData,
        course_title: selectedCourse?.title || formData.course_title
      };

      await axios.post(`${API}/api/ica/cohorts`, payload);
      toast.success('Cohort created successfully');
      setShowAddModal(false);
      resetForm();
      fetchCohorts();
    } catch (error) {
      console.error('Failed to create cohort:', error);
      toast.error('Failed to create cohort');
    }
  };

  const handleUpdateCohort = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/api/ica/cohorts/${editingCohort.cohort_id}`, formData);
      toast.success('Cohort updated successfully');
      setEditingCohort(null);
      resetForm();
      fetchCohorts();
    } catch (error) {
      console.error('Failed to update cohort:', error);
      toast.error('Failed to update cohort');
    }
  };

  const handleDeleteCohort = async (cohortId) => {
    if (!window.confirm('Are you sure you want to delete this cohort?')) return;

    try {
      await axios.delete(`${API}/api/ica/cohorts/${cohortId}`);
      toast.success('Cohort deleted successfully');
      fetchCohorts();
    } catch (error) {
      console.error('Failed to delete cohort:', error);
      toast.error('Failed to delete cohort');
    }
  };

  const handleEditClick = (cohort) => {
    setEditingCohort(cohort);
    setFormData({
      course_id: cohort.course_id,
      course_title: cohort.course_title,
      start_date: cohort.start_date?.split('T')[0] || '',
      end_date: cohort.end_date?.split('T')[0] || '',
      enrollment_deadline: cohort.enrollment_deadline?.split('T')[0] || '',
      max_seats: cohort.max_seats || 30,
      seats_available: cohort.seats_available || cohort.max_seats || 30,
      status: cohort.status || 'open',
      notes: cohort.notes || ''
    });
  };

  const resetForm = () => {
    setFormData({
      course_id: '',
      course_title: '',
      start_date: '',
      end_date: '',
      enrollment_deadline: '',
      max_seats: 30,
      notes: ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-emerald-500/20 text-emerald-400',
      closed: 'bg-red-500/20 text-red-400',
      in_progress: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-slate-500/20 text-slate-400'
    };
    return styles[status] || styles.open;
  };

  const filteredCohorts = filterCourse === 'all' 
    ? cohorts 
    : cohorts.filter(c => c.course_id === filterCourse);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Academy Cohort Management</h2>
          <p className="text-slate-400 text-sm">Manage intake dates for Advanced, Diploma, and Postgraduate courses</p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900"
          data-testid="add-cohort-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Cohort
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-400">Filter by course:</label>
          <select
            value={filterCourse}
            onChange={(e) => setFilterCourse(e.target.value)}
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
          >
            <option value="all">All Courses</option>
            {courses.map(course => (
              <option key={course.course_id} value={course.course_id}>
                {course.title}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <input
            type="checkbox"
            id="showArchived"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-4 w-4 rounded border-slate-600 bg-slate-800"
          />
          <label htmlFor="showArchived" className="text-sm text-slate-400">
            Show archived cohorts
          </label>
        </div>
      </div>

      {/* Cohorts Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading cohorts...</div>
      ) : filteredCohorts.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
          <Calendar className="h-12 w-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No cohorts found</p>
          <p className="text-sm text-slate-500 mt-1">Add your first cohort to get started</p>
        </div>
      ) : (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Course</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Start Date</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">End Date</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Enrollment Deadline</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Seats</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Status</th>
                <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredCohorts.map(cohort => (
                <tr key={cohort.cohort_id} className={`hover:bg-slate-800/50 ${!cohort.active ? 'opacity-50' : ''}`}>
                  <td className="p-4">
                    <div className="text-white font-medium">{cohort.course_title}</div>
                    <div className="text-xs text-slate-500">{cohort.course_id}</div>
                  </td>
                  <td className="p-4 text-slate-300">
                    {cohort.start_date ? new Date(cohort.start_date).toLocaleDateString('en-GB', { 
                      day: 'numeric', month: 'short', year: 'numeric' 
                    }) : '-'}
                  </td>
                  <td className="p-4 text-slate-300">
                    {cohort.end_date ? new Date(cohort.end_date).toLocaleDateString('en-GB', { 
                      day: 'numeric', month: 'short', year: 'numeric' 
                    }) : '-'}
                  </td>
                  <td className="p-4 text-slate-300">
                    {cohort.enrollment_deadline ? new Date(cohort.enrollment_deadline).toLocaleDateString('en-GB', { 
                      day: 'numeric', month: 'short', year: 'numeric' 
                    }) : '-'}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-slate-500" />
                      <span className="text-slate-300">{cohort.seats_available}/{cohort.max_seats}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(cohort.status)}`}>
                      {cohort.status || 'open'}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditClick(cohort)}
                        className="text-slate-400 hover:text-white"
                        data-testid={`edit-cohort-${cohort.cohort_id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCohort(cohort.cohort_id)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-cohort-${cohort.cohort_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || editingCohort) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-2xl max-w-lg w-full border border-slate-700 shadow-2xl">
            <div className="p-6 border-b border-slate-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">
                  {editingCohort ? 'Edit Cohort' : 'Add New Cohort'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingCohort(null);
                    resetForm();
                  }}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-slate-400" />
                </button>
              </div>
            </div>
            <form onSubmit={editingCohort ? handleUpdateCohort : handleAddCohort} className="p-6 space-y-4">
              {/* Course Selection */}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Course *</label>
                <select
                  value={formData.course_id}
                  onChange={(e) => {
                    const course = courses.find(c => c.course_id === e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      course_id: e.target.value,
                      course_title: course?.title || ''
                    }));
                  }}
                  className="w-full h-10 px-3 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
                  required
                  disabled={!!editingCohort}
                >
                  <option value="">Select a course...</option>
                  {courses.map(course => (
                    <option key={course.course_id} value={course.course_id}>
                      {course.title} ({course.provider})
                    </option>
                  ))}
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Start Date *</label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>

              {/* End Date */}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">End Date</label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              {/* Enrollment Deadline */}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Enrollment Deadline</label>
                <Input
                  type="date"
                  value={formData.enrollment_deadline}
                  onChange={(e) => setFormData(prev => ({ ...prev, enrollment_deadline: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              {/* Seats */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Max Seats</label>
                  <Input
                    type="number"
                    value={formData.max_seats}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_seats: parseInt(e.target.value) || 30 }))}
                    className="bg-slate-800 border-slate-700 text-white"
                    min="1"
                  />
                </div>
                {editingCohort && (
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Seats Available</label>
                    <Input
                      type="number"
                      value={formData.seats_available}
                      onChange={(e) => setFormData(prev => ({ ...prev, seats_available: parseInt(e.target.value) || 0 }))}
                      className="bg-slate-800 border-slate-700 text-white"
                      min="0"
                    />
                  </div>
                )}
              </div>

              {/* Status (Edit only) */}
              {editingCohort && (
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full h-10 px-3 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
                  >
                    <option value="open">Open for Enrollment</option>
                    <option value="closed">Closed</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm resize-none"
                  rows={2}
                  placeholder="Optional notes about this cohort..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingCohort(null);
                    resetForm();
                  }}
                  className="flex-1 border-slate-700 text-white"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingCohort ? 'Update Cohort' : 'Create Cohort'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
